/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ArrowRight, Quotes, ShieldCheck, Buildings, Globe, Lightning,
  Sparkle, Compass, Tree,
} from "@phosphor-icons/react";
import {
  MarketingNav, MarketingFooter, Eyebrow, BTN_GREEN,
} from "./_shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PILLAR_ICONS = [ShieldCheck, Buildings, Lightning];

export default function AboutUs() {
  const [data, setData] = useState(null);
  useEffect(() => {
    document.title = "About Us · Rescigo Health";
    window.scrollTo(0, 0);
    axios.get(`${API}/public/about`)
      .then(r => setData(r.data))
      .catch(() => setData({}));
  }, []);

  // While loading, show a thin skeleton so SEO crawlers still see nav/footer
  const d = data || {};

  return (
    <div className="min-h-screen bg-white" data-testid="page-about">
      <MarketingNav/>

      {/* ============================ HERO ============================ */}
      <section className="relative pt-36 pb-24 overflow-hidden">
        <div aria-hidden className="absolute inset-0">
          <div className="absolute -top-32 -left-20 w-[480px] h-[480px] rounded-full opacity-20 blur-3xl"
                style={{ background: "radial-gradient(circle, #2D5A27 0%, transparent 60%)" }}/>
          <div className="absolute top-40 -right-32 w-[520px] h-[520px] rounded-full opacity-15 blur-3xl"
                style={{ background: "radial-gradient(circle, #0A2540 0%, transparent 60%)" }}/>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                       transition={{ duration: 0.6 }}>
            <Eyebrow>{d.hero_eyebrow || "About Rescigo"}</Eyebrow>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl mt-5 text-slate-900 leading-[1.05] max-w-4xl">
              {d.hero_title || "A civic ecosystem where no citizen is left behind."}
            </h1>
            <p className="text-lg md:text-xl text-slate-600 mt-6 max-w-2xl leading-relaxed">
              {d.hero_subtitle || "Smart, scientific, and sustainable public services."}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ===================== CEO SPOTLIGHT ===================== */}
      <section className="relative bg-slate-50 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid md:grid-cols-12 gap-10 items-center">
          {/* Photo */}
          <motion.div
            initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.7 }}
            className="md:col-span-5 relative" data-testid="ceo-photo">
            <div className="relative aspect-[4/5] max-w-md mx-auto md:mx-0">
              {/* Decorative offset frame */}
              <div className="absolute -inset-3 bg-gradient-to-br from-[#2D5A27] to-[#0A2540] rounded-[2.5rem] opacity-90 rotate-[-2deg]"/>
              <div className="absolute inset-0 rounded-[2.5rem] overflow-hidden ring-4 ring-white shadow-2xl">
                {d.ceo_photo_url && (
                  <img src={d.ceo_photo_url} alt={d.ceo_name || "CEO"}
                        className="w-full h-full object-cover grayscale-[0.15]"/>
                )}
              </div>
              {/* Floating credential badge */}
              <div className="absolute -bottom-5 -right-5 bg-white rounded-2xl shadow-xl px-4 py-3 flex items-center gap-2.5 ring-1 ring-slate-200">
                <div className="w-8 h-8 rounded-full bg-[#2D5A27] grid place-items-center text-white">
                  <Sparkle size={14} weight="duotone"/>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Founder &amp; CEO</div>
                  <div className="text-sm font-heading text-slate-900">{d.ceo_name || "Lekshmi Manjunath"}</div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Copy */}
          <motion.div
            initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.7 }}
            className="md:col-span-7">
            <Eyebrow>Leadership</Eyebrow>
            <h2 className="font-heading text-3xl md:text-4xl text-slate-900 mt-4 leading-tight">
              {d.ceo_section_title}
            </h2>
            <div className="mt-5 space-y-4 text-slate-600 leading-relaxed">
              {(d.ceo_section_body || "").split("\n\n").map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>

            {/* Quote */}
            {d.ceo_quote && (
              <blockquote className="mt-8 relative pl-7 border-l-4 border-[#2D5A27]" data-testid="ceo-quote">
                <Quotes size={20} weight="fill" color="#2D5A27"
                         className="absolute -left-2.5 -top-1 bg-slate-50"/>
                <p className="text-lg md:text-xl italic text-slate-800 leading-snug">
                  {d.ceo_quote}
                </p>
                <footer className="mt-3 text-sm text-slate-500">
                  — <span className="font-medium text-slate-900">{d.ceo_name}</span>
                  <span className="text-slate-400"> · {d.ceo_title}</span>
                </footer>
              </blockquote>
            )}
          </motion.div>
        </div>
      </section>

      {/* ===================== IMPACT STATS ===================== */}
      <section className="py-20 md:py-24 bg-white" data-testid="impact-stats">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <Eyebrow>Our impact so far</Eyebrow>
            <h2 className="font-heading text-3xl md:text-4xl text-slate-900 mt-3">
              Numbers that mark the journey
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {(d.impact_stats || []).map((s, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-2xl p-6 hover:border-[#2D5A27] hover:shadow-lg transition-all">
                <div className="font-heading text-3xl md:text-4xl text-[#2D5A27] leading-none">{s.value}</div>
                <div className="text-sm font-medium text-slate-900 mt-3">{s.label}</div>
                {s.sub && <div className="text-xs text-slate-500 mt-1">{s.sub}</div>}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== LEGACY ===================== */}
      <section className="bg-[#0A2540] text-white py-20 md:py-24 relative overflow-hidden">
        <div aria-hidden className="absolute inset-0 opacity-20"
              style={{ background: "radial-gradient(circle at 20% 30%, #2D5A27 0%, transparent 50%)" }}/>
        <div className="relative max-w-4xl mx-auto px-6 lg:px-12">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] font-semibold text-emerald-300">
            <span className="w-6 h-px bg-emerald-300"/> Kerala &amp; beyond
          </div>
          <h2 className="font-heading text-3xl md:text-5xl mt-5 leading-tight">
            {d.legacy_section_title}
          </h2>
          <p className="text-slate-300 mt-6 text-lg leading-relaxed max-w-3xl">
            {d.legacy_section_body}
          </p>
          <div className="mt-10 flex items-center gap-5 flex-wrap">
            <Pill Icon={ShieldCheck} text="BMW Rules 2016 compliant"/>
            <Pill Icon={Buildings} text="LSGD partnerships"/>
            <Pill Icon={Globe} text="Citizen-first design"/>
          </div>
        </div>
      </section>

      {/* ===================== VISION + PILLARS ===================== */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Eyebrow>Our vision</Eyebrow>
            <h2 className="font-heading text-3xl md:text-4xl text-slate-900 mt-4 leading-tight">
              {d.vision_section_title}
            </h2>
            <div className="mt-5 space-y-4 text-slate-600 leading-relaxed">
              {(d.vision_section_body || "").split("\n\n").map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          </div>
          <div className="md:col-span-7 grid sm:grid-cols-3 gap-4">
            {(d.pillars || []).map((p, i) => {
              const Icon = PILLAR_ICONS[i % PILLAR_ICONS.length];
              return (
                <motion.div key={i}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-xl hover:-translate-y-1 transition-all">
                  <div className="w-11 h-11 rounded-lg grid place-items-center mb-5 bg-[#2D5A27]/10 text-[#2D5A27]">
                    <Icon size={20} weight="duotone"/>
                  </div>
                  <h3 className="font-heading text-lg text-slate-900">{p.title}</h3>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed">{p.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===================== CTA ===================== */}
      <section className="relative overflow-hidden bg-[#0A2540] text-white">
        <div aria-hidden className="absolute inset-0 opacity-10"
              style={{ background: "radial-gradient(circle at 80% 60%, #2D5A27 0%, transparent 50%)" }}/>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-20 md:py-24 text-center">
          <Eyebrow color="#FFFFFF66">Get in touch</Eyebrow>
          <h2 className="font-heading text-4xl md:text-5xl mt-4 max-w-3xl mx-auto leading-tight">
            {d.cta_title}
          </h2>
          {d.cta_body && (
            <p className="text-lg text-slate-300 mt-5 max-w-2xl mx-auto leading-relaxed">{d.cta_body}</p>
          )}
          <div className="mt-9 flex flex-wrap justify-center items-center gap-3">
            {d.cta_primary_label && (
              <Link to={d.cta_primary_to || "/contact"} data-testid="about-cta-primary"
                     className={BTN_GREEN}>
                {d.cta_primary_label} <ArrowRight size={14} weight="bold"/>
              </Link>
            )}
            {d.cta_secondary_label && (
              <Link to={d.cta_secondary_to || "/for-government"} data-testid="about-cta-secondary"
                     className="inline-flex items-center gap-2 h-12 px-5 rounded-full border border-white/30 text-white hover:bg-white/10 transition text-sm font-medium">
                {d.cta_secondary_label}
              </Link>
            )}
          </div>
        </div>
      </section>

      <MarketingFooter/>
    </div>
  );
}

function Pill({ Icon, text }) {
  return (
    <span className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white/10 border border-white/20 text-sm">
      <Icon size={13} weight="bold" className="text-emerald-300"/> {text}
    </span>
  );
}
